import {
  AssistantRuntimeProvider,
  Tools,
  useAui,
  useAuiState,
  useRemoteThreadListRuntime,
} from '@assistant-ui/react';
import { AssistantChatTransport, useChatRuntime } from '@assistant-ui/react-ai-sdk';
import { useNavigate } from '@tanstack/react-router';
import { type ReactNode, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/** Single-call shape of `useTranslation().t` (positional args only). */
type TLike = (key: string, vars?: Record<string, string | number>) => string;
import { CitationDataUI } from '@/lib/citation-data-ui';
import { CitationStoreProvider } from '@/lib/citation-store';
import { env } from '@/lib/env';
import { makeHistoryAdapter } from '@/lib/history-adapter';
import { extractAttachmentsFromMessages } from '@/lib/prepare-send-messages';
import { useStickyAttachmentsStore } from '@/lib/sticky-attachments-store';
import { createThreadListAdapter } from '@/lib/thread-list-adapter';
import { appToolkit } from '@/lib/toolkit';
import { createUploadAdapter } from '@/lib/upload-adapter';
import { ActiveThreadUrlSync } from './active-thread-url-sync';

export interface ChatRuntimeProviderProps {
  /**
   * The thread id from the URL (`/chat/:threadId`), or `null` when the user
   * is on `/chat` with no thread bound. Passed to `useRemoteThreadListRuntime`
   * as `threadId`; the active thread switches automatically when this value
   * changes, so URL transitions never remount the runtime or tear down an
   * in-flight stream.
   */
  urlThreadId: string | null;
  children: ReactNode;
}

/**
 * Top-level runtime provider for the chat shell. This mounts both the
 * AssistantChatTransport (sends + streams) and the RemoteThreadListAdapter
 * (sidebar list / create / delete / rename), so it must wrap the entire
 * chat layout — sidebar AND the chat surface — for `ThreadListPrimitive`
 * to read state.
 */
export function ChatRuntimeProvider({ urlThreadId, children }: ChatRuntimeProviderProps) {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();

  const tRef = useRef(t);
  tRef.current = t;
  const i18nRef = useRef(i18n);
  i18nRef.current = i18n;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const onInitialized = useCallback((remoteId: string) => {
    // The runtime is already switched to this remoteId; sync the URL. Use
    // replaceState rather than navigate({to}) so the in-flight stream
    // survives — TanStack calls the same route component with new params.
    // The sidebar list needs no refresh here: the optimistic `initialize`
    // update already prepended the newly-minted thread to `threadIds`.
    if (typeof window !== 'undefined' && window.location.pathname === '/chat') {
      const newPath = `/chat/${remoteId}${window.location.search}${window.location.hash}`;
      window.history.replaceState(null, '', newPath);
    }
  }, []);

  const onDeleted = useCallback((remoteId: string) => {
    // The runtime's `delete` optimistic update drops the thread from
    // `threadIds`; the refresh after the server-side mutation goes through
    // the adapter's `reload` callback, wired below. When the deleted thread
    // is the active one, navigate back to `/chat`.
    if (typeof window !== 'undefined' && window.location.pathname === `/chat/${remoteId}`) {
      void navigateRef.current({ to: '/chat' });
    }
  }, []);

  // The adapter needs `reload()` to ask the runtime to re-fetch its thread
  // list after rename / delete / generateTitle. The runtime instance only
  // exists after `useRemoteThreadListRuntime` runs (below), so we proxy via
  // a ref the runtime fills in on every render. The lambda passed to the
  // adapter stays stable across renders to keep the `useMemo` deps honest.
  const runtimeRef = useRef<ReturnType<typeof useRemoteThreadListRuntime> | null>(null);
  const reload = useCallback(() => {
    // `runtime.threads.reload()` bumps the load generation, clears the
    // promise cache, and re-fires `list()`.
    runtimeRef.current?.threads.reload();
  }, []);

  const threadListAdapter = useMemo(
    () =>
      createThreadListAdapter({
        t: () => tRef.current,
        locale: () => i18nRef.current.language ?? 'en',
        onInitialized,
        onDeleted,
        reload,
      }),
    [onInitialized, onDeleted, reload],
  );

  const runtime = useRemoteThreadListRuntime({
    threadId: urlThreadId ?? undefined,
    adapter: threadListAdapter,
    runtimeHook: useChatRuntimeWithTransport,
  });
  runtimeRef.current = runtime;

  // Welcome suggestions are rendered inline in `thread.tsx` via
  // `ThreadPrimitive.Suggestion` so they re-localize on a UI-language switch.
  // The runtime `Suggestions()` data path snapshots its strings at resource
  // creation and never re-reads them.
  const aui = useAui({
    tools: Tools({ toolkit: appToolkit }),
  });

  return (
    <AssistantRuntimeProvider aui={aui} runtime={runtime}>
      <CitationStoreProvider>
        <CitationDataUI />
        <ActiveThreadUrlSync urlThreadId={urlThreadId} />
        {children}
      </CitationStoreProvider>
    </AssistantRuntimeProvider>
  );
}

/**
 * The per-thread runtime hook called by `useRemoteThreadListRuntime`.
 *
 * This hook is invoked once per thread; the `aui` client captured here is
 * scoped to one specific thread's `threadListItem`. Inside
 * `prepareSendMessagesRequest` the remoteId is resolved by awaiting
 * `aui.threadListItem().initialize()`.
 *
 * `initialize()` is:
 *   - **idempotent** — the threadListItem caches the promise in
 *     `initPromiseRef`, so an already-minted thread returns the existing
 *     `{remoteId}` without re-firing `POST /threads`;
 *   - **per-thread** — each runtime instance has its own `threadListItem`,
 *     so there is no global state to leak across threads;
 *   - **race-free** — remoteId is read directly from the resolved value of
 *     `initialize()` rather than from a separately-mutated zustand slot, so
 *     the lazy-mint flow (first send from a `/chat` draft) works reliably
 *     even when state batching has not flushed yet.
 */
function useChatRuntimeWithTransport() {
  const aui = useAui();
  const auiRef = useRef(aui);
  auiRef.current = aui;
  const remoteIdRef = useRef<string | null>(null);
  // Sync the history-adapter's ref from per-thread Aui state during
  // render — `history.load()` runs immediately on thread switch, before
  // any user send, so we can't rely on `prepareSendMessagesRequest` to
  // populate it.
  const renderRemoteId = useAuiState((s) => s.threadListItem.remoteId) as string | undefined;
  remoteIdRef.current = renderRemoteId ?? null;

  // i18n is per-thread because each thread mounts its own runtime, but the
  // `t` function still rotates with locale changes — keep a ref that points
  // at the latest binding so the upload adapter (which lives outside React)
  // localizes error messages with the current language.
  const { t } = useTranslation();
  const callT: TLike = (k, v) => (v ? t(k, v) : t(k));
  const tRef = useRef<TLike>(callT);
  tRef.current = callT;

  // Composer-attachment snapshot for the upload adapter's per-batch cap
  // pre-validation. The adapter lives outside the React tree (captured by a
  // `useMemo` closure) so a live ref always points at the latest composer
  // state. This hook re-renders on every composer mutation, so the ref is
  // fresh inside the next `add()` call.
  type ComposerAttSlice = {
    file?: { size?: number } | undefined;
    contentType?: string | undefined;
  };
  const composerAttachments = useAuiState(
    (s) => (s as { composer: { attachments: readonly unknown[] } }).composer.attachments,
  ) as ReadonlyArray<ComposerAttSlice>;
  const composerAttachmentsRef = useRef<ReadonlyArray<ComposerAttSlice>>(composerAttachments);
  composerAttachmentsRef.current = composerAttachments;

  // Sticky-thread totals come from the Zustand store, which
  // `<StickyThreadAttachments />` updates after every `GET /uploads?threadId=`
  // refetch. We READ via `getState()` inside the adapter's `usage` getters
  // (no React subscription) — the latest store snapshot is always
  // synchronously available since the strip mounts above the composer.
  const transport = useMemo(
    () =>
      new AssistantChatTransport({
        api: '',
        credentials: 'include',
        // The server's ChatRequestBody is `.strict()` and only accepts
        // `{messages, system?, tools?, locale?, attachments?}`; the AI SDK
        // default body would tack on `id/trigger/messageId/...` and 422
        // every send. Rewrite both the URL and the body here.
        prepareSendMessagesRequest: async (options) => {
          let remoteId: string | undefined;
          try {
            const result = await auiRef.current.threadListItem?.().initialize();
            remoteId = result?.remoteId;
          } catch (err) {
            throw new Error(
              `Cannot send: thread initialize failed (${err instanceof Error ? err.message : String(err)})`,
            );
          }
          if (!remoteId) {
            throw new Error('Cannot send: no active thread');
          }
          // Extract upload refs from the last user message's `file` parts so
          // the server sees them in `body.attachments` (the strict contract
          // field) AND so we never inline the file bytes into the chat body.
          const attachments = extractAttachmentsFromMessages(
            options.messages as ReadonlyArray<{
              role?: string;
              parts?: ReadonlyArray<unknown>;
            }>,
          );
          // Branch parent of the new turn. The "previous message in the
          // active path" is read off the wire-shaped `messages` array since
          // `prepareSendMessagesRequest` lacks a message context. Two
          // outcomes:
          //   - len >= 2 AND the prior message's id is a UUID (server-issued
          //     ids adopted by streamed assistant messages and history-loaded
          //     user messages) → send it. The server validates against
          //     `chat_messages.id` for this thread; a mismatch degrades to
          //     auto-resolve.
          //   - len === 1 OR the prior id is an in-session nanoid (a client
          //     mint that never persisted) → send explicit `null`, so an edit
          //     of the first user message writes a sibling under the NULL root
          //     instead of stitching under the latest message in the thread.
          const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          const msgs = options.messages as ReadonlyArray<{ id?: unknown }>;
          const priorId = msgs.length >= 2 ? msgs[msgs.length - 2]?.id : undefined;
          const parentMessageId: string | null =
            typeof priorId === 'string' && UUID_RE.test(priorId) ? priorId : null;
          // Forward AI SDK's `trigger` + `messageId` so the server can route
          // parent_id resolution by intent. For `edit-message` the server
          // pulls the source message's parent from the DB instead of trusting
          // `parentMessageId`. `messageId` is forwarded only when it's a UUID
          // (history-loaded ids), since AI-SDK-minted nanoids would not match
          // anything server-side.
          const optsExt = options as {
            trigger?: 'submit-message' | 'edit-message' | 'regenerate-message';
            messageId?: unknown;
          };
          const triggerOut = optsExt.trigger;
          const messageIdOut =
            typeof optsExt.messageId === 'string' && UUID_RE.test(optsExt.messageId)
              ? optsExt.messageId
              : undefined;
          return {
            api: `${env.VITE_API_BASE_URL}/threads/${remoteId}/messages`,
            body: {
              messages: options.messages,
              parentMessageId,
              ...(triggerOut ? { trigger: triggerOut } : {}),
              ...(messageIdOut ? { messageId: messageIdOut } : {}),
              ...(attachments.length > 0 ? { attachments } : {}),
            },
          };
        },
      }),
    [],
  );

  const history = useMemo(() => makeHistoryAdapter(remoteIdRef), []);

  const uploadAdapter = useMemo(
    () =>
      createUploadAdapter({
        threadId: () => remoteIdRef.current,
        usage: {
          composerBytes: () =>
            composerAttachmentsRef.current.reduce((sum, a) => sum + (a.file?.size ?? 0), 0),
          composerImageCount: () =>
            composerAttachmentsRef.current.filter(
              (a) => typeof a.contentType === 'string' && a.contentType.startsWith('image/'),
            ).length,
          stickyBytes: () => useStickyAttachmentsStore.getState().bytes,
          stickyImageCount: () => useStickyAttachmentsStore.getState().imageCount,
        },
        t: (key, vars) => tRef.current(key, vars),
      }),
    [],
  );

  return useChatRuntime({ transport, adapters: { history, attachments: uploadAdapter } });
}
